import { createContext, useCallback, useEffect, useState } from "react";
import type { FC, ReactNode } from "react";
import PropTypes from "prop-types";
import { aicLockingService } from "src/services/reporting/aicLockingService";
import { HTTPSTATUSCODES } from "src/utils/constants";
import useAuth from "src/hooks/useAuth";

export interface AicLockContextValue {
	isAicLocked: boolean;
	loading: boolean;
	setAicLocked: (locked: boolean) => void;
	refresh: () => Promise<void>;
}

interface AicLockProviderProps {
	children?: ReactNode;
}

const AicLockContext = createContext<AicLockContextValue>({
	isAicLocked: false,
	loading: true,
	setAicLocked: () => {},
	refresh: async () => {},
});

export const AicLockProvider: FC<AicLockProviderProps> = ({ children }) => {
	const { isAuthenticated } = useAuth();
	const [isAicLocked, setIsAicLocked] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);

	const refresh = useCallback(async () => {
		try {
			const response = await aicLockingService.getAicLockStatus();
			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				setIsAicLocked(Boolean(response.data?.isLocked));
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!isAuthenticated) {
			setIsAicLocked(false);
			setLoading(false);

			return;
		}

		setLoading(true);
		refresh();
	}, [isAuthenticated, refresh]);

	return (
		<AicLockContext.Provider
			value={{
				isAicLocked,
				loading,
				setAicLocked: setIsAicLocked,
				refresh,
			}}
		>
			{children}
		</AicLockContext.Provider>
	);
};

AicLockProvider.propTypes = {
	children: PropTypes.node,
};

export default AicLockContext;
